'use client';

import ListViewSkeleton from '@/components/skeletons/ListViewSkeleton';
import useFullMap from '@/hooks/useFullMap';
import type { RequestQuoteButtonStrings } from '@/utils/venueCardHelper';
import type { VenueHighlightStrings } from '@/utils/venueHighlightsHelper';
import type { ShoppingCartItem } from '@/models/ShoppingCartItem';
import type { SearchParams } from '@/models/SearchParams';
import type { Dictionary } from '@/dictionaries';
import type { MeasurementUnit } from '@cvent/venue-search-shared/types';
import FullMapVenueCard from '@/components/maps/list-view/FullMapVenueCard';
import type { Locale } from '@/i18n';
import type { SearchResultsRank } from '@/utils/analyticsHelper';
import { getSearchResultRankForVenues } from '@/utils/analyticsHelper';
import VenueHighlights from '@/components/card/VenueHighlights';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import EmptyResults from '@/components/EmptyResults';
import type { SearchBannerCookieDataType } from '@/models/SearchBannerCookieData';
import { VenueCardContainer } from '@/components/card/VenueCardContainer';
import type { CertifiedViewExperimentData } from '@/analytics/models/cventAnalytics/CertifiedViewFact';
import FullMapDiamondPlusAd from '@/components/maps/list-view/FullMapDiamondPlusAd';
import type { Criteria } from '@/fetchers/getFastResponderCriteria';

const FullMapVenueCardTags = dynamic(() => import('@/components/maps/list-view/FullMapVenueCardTags'), {
  ssr: false,
  loading: () => <div className="bg-shimmer h-6 w-full animate-shimmer rounded-sm bg-[length:200%_100%]" />
});

export type FullMapListViewStrings = {
  venueImageAltText: string;
  screenReaderText: string;
  requestQuoteButtonStrings: RequestQuoteButtonStrings;
  ratingAndReviewStrings: {
    csn_search_venue_card_distance_km: string;
    csn_search_venue_card_distance_mi: string;
    csn_search_venue_card_distance_tooltip: string;
  };
  venueHighlightStrings: VenueHighlightStrings;
  promoted: string;
  fastResponderStrings: {
    fast_responder_tag_tooltip: string;
    top_responder: string;
  };
};

type ListViewProps = {
  locale: Locale;
  isLoggedIn: boolean;
  activeFilters: string;
  eventStartDate?: Date;
  eventEndDate?: Date;
  searchParams: SearchParams;
  cventGuestDomain: string;
  basePath: string;
  certifiedViewExperimentData: CertifiedViewExperimentData;
  accountId: string;
  environment: string;
  isRFPFullAccess: boolean;
  rfpSupplierIds: Array<ShoppingCartItem>;
  measurementUnit: MeasurementUnit;
  canShowDistanceOnCards: boolean;
  fullMapListViewStrings: FullMapListViewStrings;
  localeStrings: Dictionary;
  searchTerm: string;
  page: number;
  rfpId: string | null;
  searchBannerData: SearchBannerCookieDataType;
  fastResponderCriteria: Criteria;
  isFastResponderEnabled: number;
  userAccountCustomPromotionText?: string | undefined;
};

function getNoResultsFoundLocaleStrings(localeStrings: Dictionary) {
  return {
    _do_not_see_a_supplier: localeStrings._do_not_see_a_supplier,
    _request_to_add_a_venue_to_cvent_supplier_netwrok: localeStrings._request_to_add_a_venue_to_cvent_supplier_netwrok,
    _send_a_request: localeStrings._send_a_request,
    _reset_filters: localeStrings.filters_all_filters_reset_button,
    _csn_search_srp_no_results_page_expand_search_criteria_message:
      localeStrings._csn_search_srp_no_results_page_expand_search_criteria_message,
    _csn_search_srp_no_results_page_no_results_found_message:
      localeStrings._csn_search_srp_no_results_page_no_results_found_message
  };
}

export default function FullMapListView({
  locale,
  isLoggedIn,
  activeFilters,
  eventStartDate,
  eventEndDate,
  searchParams,
  cventGuestDomain,
  basePath,
  certifiedViewExperimentData,
  accountId,
  environment,
  isRFPFullAccess,
  rfpSupplierIds,
  measurementUnit,
  canShowDistanceOnCards,
  fullMapListViewStrings,
  localeStrings,
  searchTerm,
  page,
  rfpId,
  searchBannerData,
  fastResponderCriteria,
  isFastResponderEnabled,
  userAccountCustomPromotionText
}: ListViewProps): JSX.Element {
  const { isListViewLoading, mapVenuesForListView, mapShoppingCartItems } = useFullMap();
  const searchResultRankForVenues: SearchResultsRank = useMemo(
    () => getSearchResultRankForVenues(mapVenuesForListView, page),
    [mapVenuesForListView, page]
  );

  function renderSearchResult() {
    if (!mapVenuesForListView || mapVenuesForListView.length === 0) {
      const noResultsPageStrings = getNoResultsFoundLocaleStrings(localeStrings);
      const appliedFilters = JSON.parse(activeFilters);
      return (
        <EmptyResults
          locale={locale}
          noResultsPageStrings={noResultsPageStrings}
          appliedFilters={appliedFilters}
          isLoggedIn={isLoggedIn}
          rfpId={rfpId}
          isLoginBannerDismissed={searchBannerData.isLoginBannerDismissed}
        />
      );
    }
    return (
      <ul>
        {mapVenuesForListView.map((venue, index) => {
          let searchResultsRank = searchResultRankForVenues.venues[venue.id];
          const { id, featuredLevel, geographicLocation, adText, adTagline, listingOwnerType, listingCode } = venue;
          if (featuredLevel === 'DIAMOND_PLUS') {
            searchResultsRank = searchResultRankForVenues.diamondPlusAds[venue.id];
          }
          if (featuredLevel === 'FOUR_DIAMOND') {
            searchResultsRank = searchResultRankForVenues.fourDiamondAds[venue.id];
          }

          const isBrandPage = featuredLevel && listingOwnerType === 'BRAND_PAGE';
          const hideVenueStats = Boolean(isBrandPage || (featuredLevel === 'DIAMOND_PLUS' && adText));
          let adType = featuredLevel;
          if (featuredLevel === 'DIAMOND_PLUS') {
            adType = hideVenueStats ? 'DIAMOND_PLUS_TEXT' : 'DIAMOND_PLUS_STATS';
          }
          const shoppingCartItem = mapShoppingCartItems.get(id);
          const { url: profileUrl } = shoppingCartItem || {};
          const { venueHighlightStrings } = fullMapListViewStrings;
          if (featuredLevel === 'DIAMOND_PLUS') {
            return (
              <li key={id.concat(featuredLevel || '')}>
                <VenueCardContainer
                  certifiedViewExperimentData={certifiedViewExperimentData}
                  venueIndex={index}
                  venue={venue}
                  searchTerm={searchTerm}
                  environment={environment}
                  isLoggedIn={isLoggedIn}
                  accountId={accountId}
                  adType={adType}
                  locale={locale}
                  searchResultsRank={searchResultsRank}
                  searchParams={searchParams}
                  cventGuestDomain={cventGuestDomain}
                  basePath={basePath}
                  activeFilters={activeFilters}
                >
                  <FullMapDiamondPlusAd
                    venue={venue}
                    adTagline={adTagline}
                    venueIndex={index}
                    locale={locale}
                    isLoggedIn={isLoggedIn}
                    activeFilters={activeFilters}
                    isRFPFullAccess={isRFPFullAccess}
                    fullMapListViewStrings={fullMapListViewStrings}
                    shoppingCartItem={shoppingCartItem}
                    measurementUnit={measurementUnit}
                  />
                </VenueCardContainer>
              </li>
            );
          }
          if (geographicLocation || featuredLevel === 'FOUR_DIAMOND') {
            return (
              <li key={id.concat(featuredLevel || '')}>
                <VenueCardContainer
                  certifiedViewExperimentData={certifiedViewExperimentData}
                  venueIndex={index}
                  venue={venue}
                  searchTerm={searchTerm}
                  environment={environment}
                  isLoggedIn={isLoggedIn}
                  accountId={accountId}
                  adType={adType}
                  locale={locale}
                  searchResultsRank={searchResultsRank}
                  searchParams={searchParams}
                  cventGuestDomain={cventGuestDomain}
                  basePath={basePath}
                  activeFilters={activeFilters}
                >
                  <FullMapVenueCard
                    venue={venue}
                    venueIndex={index}
                    locale={locale}
                    isLoggedIn={isLoggedIn}
                    hideVenueStats={hideVenueStats}
                    activeFilters={activeFilters}
                    isRFPFullAccess={isRFPFullAccess}
                    fullMapListViewStrings={fullMapListViewStrings}
                    shoppingCartItem={shoppingCartItem}
                    measurementUnit={measurementUnit}
                    canShowDistanceOnCards={canShowDistanceOnCards}
                    isListing={!!listingCode}
                    fastResponderCriteria={fastResponderCriteria}
                    isFastResponderEnabled={isFastResponderEnabled}
                  >
                    {!hideVenueStats && (
                      <div className="m-2 flex flex-col gap-2">
                        <VenueHighlights
                          venue={venue}
                          measurementUnit={measurementUnit}
                          locale={locale}
                          highlightStrings={venueHighlightStrings}
                        />

                        <FullMapVenueCardTags
                          venue={venue}
                          localeStrings={localeStrings}
                          eventStartDate={eventStartDate}
                          eventEndDate={eventEndDate}
                          locale={locale}
                          rfpSupplierIds={rfpSupplierIds}
                          profileUrl={profileUrl || ''}
                          searchResultsRank={searchResultsRank}
                          searchTerm={searchTerm}
                          venueIndex={index}
                          activeFilters={activeFilters}
                          isLoggedIn={isLoggedIn}
                          isRFPFullAccess={isRFPFullAccess}
                          userAccountCustomPromotionText={userAccountCustomPromotionText}
                        />
                      </div>
                    )}
                  </FullMapVenueCard>
                </VenueCardContainer>
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  }

  return (
    <div className="m-4 my-0 h-full w-1/4 max-w-sm">
      {isListViewLoading ? <ListViewSkeleton /> : renderSearchResult()}
    </div>
  );
}
